// src/pages/Dashboard.jsx
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  doc,
  deleteDoc,
} from 'firebase/firestore';

import { db } from '../firebase.js';
import { useAuth } from '../hooks/useAuth.js';
import { usePlanGuard } from '../hooks/usePlanGuard.js';
import SEO from '../components/SEO.jsx';

function statusBadge(status, t) {
  switch (status) {
    case 'done': return { label: t('dashboard.statusDone'), className: 'bg-green-100 text-green-700' };
    case 'rendering': return { label: t('dashboard.statusRendering'), className: 'bg-blue-100 text-blue-700' };
    case 'pending': return { label: t('dashboard.statusPending'), className: 'bg-gray-100 text-gray-700' };
    case 'failed': return { label: t('dashboard.statusFailed'), className: 'bg-red-100 text-red-700' };
    default: return { label: status || '?', className: 'bg-gray-100 text-gray-700' };
  }
}

function fmtDate(ts) {
  const d = ts?.toDate ? ts.toDate() : (ts ? new Date(ts) : null);
  if (!d || Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString();
}

export default function Dashboard() {
  const { t } = useTranslation();
  const { user, profile } = useAuth();
  const { isAdmin } = usePlanGuard();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [queryError, setQueryError] = useState(null);
  const [search, setSearch] = useState('');
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    if (!user) return undefined;
    const q = query(
      collection(db, 'videos'),
      where('uid', '==', user.uid),
      orderBy('createdAt', 'desc'),
      limit(60),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setVideos(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
        setQueryError(null);
      },
      (err) => {
        console.error('[dashboard] firestore query err:', err);
        setQueryError(err);
        setLoading(false);
      },
    );
    return unsub;
  }, [user]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return videos;
    return videos.filter((v) =>
      `${v.script?.title || ''} ${v.newsTitle || ''}`.toLowerCase().includes(q),
    );
  }, [videos, search]);

  async function handleDelete(e, v) {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm(t('dashboard.deleteConfirm'))) return;
    setDeletingId(v.id);
    try {
      await deleteDoc(doc(db, 'videos', v.id));
      toast.success(t('dashboard.deleted'));
    } catch (err) {
      toast.error(err.message || t('common.deleteFailed'));
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <SEO title={t('nav.dashboard')} description={t('dashboard.seoDesc')} path="/dashboard" noIndex />
      <div className="flex flex-wrap items-center justify-between mb-6 gap-3">
        <div>
          <h1 className="text-2xl font-bold">{t('nav.dashboard')}</h1>
          <p className="text-sm text-gray-500">
            ⚡ {t('create.creditsLeft', { count: profile?.credits ?? 0 })} ·{' '}
            <Link to="/pricing" className="underline">{t('pricing.upgradeBtn')}</Link>
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <Link to="/admin" className="btn-secondary">
              🛠 자동 생성
            </Link>
          )}
          <Link to="/create" className="btn-primary">
            ➕ {t('nav.create')}
          </Link>
        </div>
      </div>

      {!loading && !queryError && videos.length > 0 && (
        <input
          type="search"
          className="input mb-4"
          placeholder={t('dashboard.search')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      )}

      {loading ? (
        <div className="text-center py-12 text-gray-500">{t('account.loading')}</div>
      ) : queryError ? (
        <div className="card border-2 border-red-200 bg-red-50 space-y-2">
          <p className="text-sm font-bold text-red-700">{t('dashboard.queryFailed')}</p>
          <p className="text-xs text-red-600 break-all">
            {queryError.code}: {queryError.message}
          </p>
          {queryError.code === 'failed-precondition' && (
            <p className="text-xs text-gray-600">
              {t('dashboard.indexHint')}
            </p>
          )}
        </div>
      ) : videos.length === 0 ? (
        <div className="card text-center py-12">
          <div className="text-3xl mb-2">🎬</div>
          <p className="text-gray-600 mb-3">{t('dashboard.empty')}</p>
          <Link to="/create" className="btn-primary inline-block">
            ➕ {t('nav.create')}
          </Link>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-500">{t('dashboard.noResults')}</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {filtered.map((v) => {
            const badge = statusBadge(v.renderStatus, t);
            return (
              <Link
                key={v.id}
                to={`/video/${v.id}`}
                className="card !p-3 hover:shadow-md transition"
              >
                <div className="aspect-[9/16] bg-gray-100 rounded-lg overflow-hidden mb-2 relative">
                  {v.videoUrl ? (
                    <video
                      src={v.videoUrl}
                      muted
                      playsInline
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-3xl">
                      🎬
                    </div>
                  )}
                  <span
                    className={`absolute top-1 right-1 badge ${badge.className}`}
                  >
                    {badge.label}
                  </span>
                  <button
                    onClick={(e) => handleDelete(e, v)}
                    disabled={deletingId === v.id}
                    title={t('dashboard.delete')}
                    className="absolute top-1 left-1 w-7 h-7 rounded-full bg-black/50 text-white text-xs hover:bg-red-600 transition flex items-center justify-center"
                  >
                    {deletingId === v.id ? '…' : '🗑'}
                  </button>
                </div>
                <div className="text-sm font-bold line-clamp-2">
                  {v.script?.title || v.newsTitle || '—'}
                </div>
                {fmtDate(v.createdAt) && (
                  <div className="text-[11px] text-gray-400 mt-1">{fmtDate(v.createdAt)}</div>
                )}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
